/**
 * useAccountDetails
 *
 * Fetches account plan/usage data and saved settings in parallel.
 * Waits for auth token before making API requests.
 *
 * Returns:
 *  - account
 *  - settings
 *  - loading
 *  - error
 *  - reload
 *  - authReady
 */

import { useState, useEffect, useCallback } from 'react';
import { fetchAccountDetails } from '../api/accountApi';
import { fetchSettings } from '../api/settingsApi';
import { getAuthToken } from '../utils/authToken';

export const useAccountDetails = (portalId, slug) => {
  const [account, setAccount] = useState(null);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Prevent API calls before token is available
  const [authReady, setAuthReady] = useState(false);

  /**
   * Wait for auth token
   */
  useEffect(() => {
    const token = getAuthToken();

    if (token) {
      setAuthReady(true);
    }
  }, []);

  /**
   * Main loader
   */
  const load = useCallback(async () => {
    // Skip until both are ready
    if (!portalId || !authReady) {
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Parallel requests
      const [accountData, settingsData] = await Promise.all([
        fetchAccountDetails(portalId),
        fetchSettings(portalId, slug),
      ]);

      setAccount(accountData);
      setSettings(settingsData);
    } catch (err) {
      setError(err.message || 'Failed to load account details.');
    } finally {
      setLoading(false);
    }
  }, [portalId, slug, authReady]);

  /**
   * Auto-fetch on mount/change
   */
  useEffect(() => {
    load();
  }, [load]);

  return {
    account,
    settings,
    loading,
    error,
    reload: load,
    authReady,
  };
};